import { useState } from 'react';
import { skipOnboardingStep } from '../../api/endpoints.js';
import { useAction } from '../../api/hooks.js';
import { writeFlags, type Purpose } from '../../flow/machine.js';
import { goStep } from '../../flow/nav.js';
import {
  FlowScreen,
  FlowSub,
  FlowTitle,
  OptionCard,
  Pill,
  PrimaryButton,
  SecondaryButton,
} from '../../ui/flow.js';

/**
 * Mobile screens 3–5: the branch point, the rationale, and the skippable
 * assessment offer. No network anywhere in this block — the single choice made
 * here is read once, at the very end of onboarding, to decide whether family
 * setup runs.
 */

const OPTIONS: { id: Purpose; title: string }[] = [
  { id: 'just_me', title: 'Just me' },
  { id: 'my_family', title: 'My family' },
];

export function PurposeStep() {
  const [selected, setSelected] = useState<Purpose | null>(null);

  return (
    <FlowScreen progress={8}>
      <FlowTitle>Are you interested in likeness protection for yourself or your family?</FlowTitle>
      <div className="option-stack">
        {OPTIONS.map((option) => (
          <OptionCard
            key={option.id}
            selected={selected === option.id}
            disabled={selected !== null}
            onClick={() => {
              setSelected(option.id);
              writeFlags({ purpose: option.id, checkpoint: 'purpose' });
              // The card turns purple, then the screen advances by itself.
              window.setTimeout(() => goStep('info'), 200);
            }}
          >
            {option.title}
          </OptionCard>
        ))}
      </div>
    </FlowScreen>
  );
}

export function InfoStep() {
  return (
    <FlowScreen
      progress={16}
      onBack={() => window.history.back()}
      footer={<PrimaryButton onClick={() => goStep('assessment')}>Continue</PrimaryButton>}
    >
      <FlowTitle>
        Your social media sharing, age and gender are key factors in determining your risk of
        likeness theft or abuse.
      </FlowTitle>
      <FlowSub>1 in 5 people report that they know they&rsquo;ve had an image abused online.</FlowSub>
    </FlowScreen>
  );
}

export function AssessmentStep() {
  // The quiz block is bypassed entirely: no score is computed, and the dashboard
  // later shows "Take the Likeness Health Quiz" as an urgent recommended action
  // instead of a score.
  //
  // RECORDED SERVER-SIDE, not in localStorage as this once was. A skip kept here
  // vanished on sign-out and never existed on a second device, so the flow
  // restarted from the quiz — the failure a tester reported. Only on success do
  // we advance: if the call fails the user stays on this screen with the error,
  // rather than walking on with a skip nothing recorded.
  const skipQuiz = useAction(() => skipOnboardingStep('quiz'), () => {
    writeFlags({ checkpoint: null });
    goStep('plans');
  });

  return (
    <FlowScreen
      progress={24}
      onBack={() => window.history.back()}
      footer={
        <>
          <PrimaryButton onClick={() => goStep('quiz')}>
            Take the Likeness Health Quiz
          </PrimaryButton>
          <p className="flow-note" style={{ textAlign: 'center' }}>
            No worries, you can take the quiz to determine your Likeness Health Score℠ anytime
            after onboarding.
          </p>
          <SecondaryButton
            onClick={() => skipQuiz.run()}
            disabled={skipQuiz.pending}
          >
            {skipQuiz.pending ? 'Skipping…' : 'Skip for now'}
          </SecondaryButton>
          {skipQuiz.error !== null && <p className="flow-error">{skipQuiz.error.message}</p>}
        </>
      }
    >
      <FlowTitle>Determine your risk of likeness theft or abuse with our free assessment</FlowTitle>
      <FlowSub>
        Take our quiz to determine your Likeness Health Score℠, indicating how likely you are to
        have your likeness stolen or misused. We recommend it!
      </FlowSub>
      <Pill>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <circle cx="12" cy="13" r="8" stroke="currentColor" strokeWidth="2" />
          <path d="M12 9v4l2.5 2.5M9 2h6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </svg>
        Estimated time to complete: 1 minute
      </Pill>
    </FlowScreen>
  );
}
